/**
 * Handle tooltips for email and phone icons
 * Provides copy functionality and action buttons
 * Uses Floating UI for robust positioning
 */

import { computePosition, flip, shift, offset, arrow, autoUpdate } from '@floating-ui/dom';

class ContactTooltip {
    constructor() {
        this.activeTooltip = null;
        this.hideTimeout = null;
        this.showTimeout = null;
        this.cleanup = null;
        this.translations = {
            en: {
                copy: 'Copy',
                copied: 'Copied!',
                sendEmail: 'Send Email',
                call: 'Call',
                clickForOptions: 'Click for options',
                showContactOptions: 'Show contact options for',
                hideContactOptions: 'Hide contact options for',
                optionsOpened: 'Contact options opened for',
                optionsClosed: 'Contact options closed for'
            },
            de: {
                copy: 'Kopieren',
                copied: 'Kopiert!',
                sendEmail: 'E-Mail senden',
                call: 'Anrufen',
                clickForOptions: 'Klicken für Optionen',
                showContactOptions: 'Kontaktoptionen anzeigen für',
                hideContactOptions: 'Kontaktoptionen ausblenden für',
                optionsOpened: 'Kontaktoptionen geöffnet für',
                optionsClosed: 'Kontaktoptionen geschlossen für'
            },
            it: {
                copy: 'Copia',
                copied: 'Copiato!',
                sendEmail: 'Invia Email',
                call: 'Chiama',
                clickForOptions: 'Clicca per opzioni',
                showContactOptions: 'Mostra opzioni contatto per',
                hideContactOptions: 'Nascondi opzioni contatto per',
                optionsOpened: 'Opzioni contatto aperte per',
                optionsClosed: 'Opzioni contatto chiuse per'
            },
            fr: {
                copy: 'Copier',
                copied: 'Copié!',
                sendEmail: 'Envoyer Email',
                call: 'Appeler',
                clickForOptions: 'Cliquer pour options',
                showContactOptions: 'Afficher options contact pour',
                hideContactOptions: 'Masquer options contact pour',
                optionsOpened: 'Options contact ouvertes pour',
                optionsClosed: 'Options contact fermées pour'
            }
        };
        this.currentLang = this.detectLanguage();
        this.init();
    }

    detectLanguage() {
        const htmlLang = document.documentElement.lang || 'en';
        const langCode = htmlLang.substring(0, 2).toLowerCase();
        return this.translations[langCode] ? langCode : 'en';
    }

    getTranslation(key) {
        return this.translations[this.currentLang][key] || this.translations.en[key];
    }

    init() {
        this.createTooltipContainer();
        this.bindEvents();
    }

    createTooltipContainer() {
        // Create a single tooltip container that we'll reuse
        this.tooltipContainer = document.createElement('div');
        this.tooltipContainer.className = 'contact-tooltip';
        this.tooltipContainer.id = 'contact-tooltip'; // Add ID for aria-controls
        this.tooltipContainer.setAttribute('role', 'dialog');
        this.tooltipContainer.setAttribute('aria-live', 'polite');
        this.tooltipContainer.innerHTML = `
            <div class="contact-tooltip__content">
                <div class="contact-tooltip__value"></div>
                <div class="contact-tooltip__actions">
                    <button class="contact-tooltip__copy" type="button">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        <div class="contact-tooltip__copy-text">
                            <span class="contact-tooltip__copy-original">${this.getTranslation('copy')}</span>
                            <span class="contact-tooltip__copy-feedback" style="display: none;">${this.getTranslation('copied')}</span>
                        </div>
                    </button>
                    <button class="contact-tooltip__action" type="button">
                        <i class="contact-tooltip__action-icon">
                            <svg aria-hidden="true">
                                <use xlink:href="#ico-mail"></use>
                            </svg>
                        </i>
                        <span class="contact-tooltip__action-text">${this.getTranslation('sendEmail')}</span>
                    </button>
                </div>
            </div>
            <div class="contact-tooltip__arrow"></div>
        `;
        document.body.appendChild(this.tooltipContainer);

        // Create a live region for announcements
        this.liveRegion = document.createElement('div');
        this.liveRegion.setAttribute('aria-live', 'polite');
        this.liveRegion.setAttribute('aria-atomic', 'true');
        this.liveRegion.className = 'sr-only';
        this.liveRegion.style.position = 'absolute';
        this.liveRegion.style.left = '-10000px';
        this.liveRegion.style.width = '1px';
        this.liveRegion.style.height = '1px';
        this.liveRegion.style.overflow = 'hidden';
        document.body.appendChild(this.liveRegion);
    }

    bindEvents() {
        // Handle hover on team member contact links only - use specific selectors
        document.addEventListener('mouseenter', (e) => {
            const contactLink = e.target.closest('.member__actions a[href^="mailto:"], .member__actions a[href^="tel:"]');
            if (contactLink) {
                this.clearHideTimeout();
                // Add small delay to prevent interference between stacked links
                if (this.showTimeout) {
                    clearTimeout(this.showTimeout);
                }
                this.showTimeout = setTimeout(() => {
                    this.showTooltip(contactLink);
                }, 150);
            }
        }, true);

        document.addEventListener('mouseleave', (e) => {
            const contactLink = e.target.closest('.member__actions a[href^="mailto:"], .member__actions a[href^="tel:"]');
            if (contactLink) {
                // Clear show timeout if we're leaving before it triggers
                if (this.showTimeout) {
                    clearTimeout(this.showTimeout);
                    this.showTimeout = null;
                }

                // Check if we're moving to the tooltip or another contact link in same member
                const relatedTarget = e.relatedTarget;
                const isMovingToTooltip = relatedTarget && relatedTarget.closest('.contact-tooltip');
                const isMovingToSameMemberLink = relatedTarget &&
                    relatedTarget.closest('.member__actions a[href^="mailto:"], .member__actions a[href^="tel:"]') &&
                    contactLink.closest('.member__actions') === relatedTarget.closest('.member__actions');

                if (!isMovingToTooltip && !isMovingToSameMemberLink) {
                    this.scheduleHide();
                }
            }
        }, true);

        // Block click events on team member contact links when tooltip is visible
        document.addEventListener('click', (e) => {
            const contactLink = e.target.closest('.member__actions a[href^="mailto:"], .member__actions a[href^="tel:"]');
            if (contactLink && this.activeTooltip === contactLink) {
                e.preventDefault();
                e.stopPropagation();
                // Tooltip is visible, prevent default link action
                return false;
            }
        }, true);

        // Keep tooltip visible when hovering over it
        this.tooltipContainer.addEventListener('mouseenter', () => {
            this.clearHideTimeout();
        });

        this.tooltipContainer.addEventListener('mouseleave', (e) => {
            // Check if we're moving back to the same member's contact links
            const relatedTarget = e.relatedTarget;
            const isMovingToSameMemberLink = relatedTarget &&
                relatedTarget.closest('.member__actions a[href^="mailto:"], .member__actions a[href^="tel:"]') &&
                this.activeTooltip &&
                this.activeTooltip.closest('.member__actions') === relatedTarget.closest('.member__actions');

            if (!isMovingToSameMemberLink) {
                this.scheduleHide();
            }
        });

        // Handle tooltip button clicks
        document.addEventListener('click', (e) => {
            if (e.target.closest('.contact-tooltip__copy')) {
                e.preventDefault();
                this.copyValue();
            } else if (e.target.closest('.contact-tooltip__action')) {
                e.preventDefault();
                this.performAction();
            }
        });

        // Hide tooltip on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.hideTooltip();
            }
        });
    }

    clearHideTimeout() {
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
            this.showTimeout = null;
        }
    }

    scheduleHide() {
        this.clearHideTimeout();
        this.hideTimeout = setTimeout(() => {
            this.hideTooltip();
        }, 200);
    }

    async showTooltip(linkElement) {
        const href = linkElement.getAttribute('href');
        const isEmail = href.startsWith('mailto:');
        const value = isEmail ? href.replace('mailto:', '') : href.replace('tel:', '');

        // Store current context
        this.currentHref = href;
        this.currentValue = value;
        this.currentType = isEmail ? 'email' : 'phone';
        this.currentTarget = linkElement;

        // Add visual indicator that link behavior is changed
        linkElement.classList.add('tooltip-active');

        // Add descriptive aria-label to the contact link
        const displayValue = isEmail ? value : this.formatPhoneNumber(value);
        linkElement.setAttribute('aria-label', `${this.getTranslation('hideContactOptions')} ${displayValue}`);
        linkElement.setAttribute('aria-expanded', 'true');
        linkElement.setAttribute('aria-controls', 'contact-tooltip');

        // Update tooltip content
        const valueElement = this.tooltipContainer.querySelector('.contact-tooltip__value');
        const actionIconContainer = this.tooltipContainer.querySelector('.contact-tooltip__action-icon');
        const actionIcon = this.tooltipContainer.querySelector('.contact-tooltip__action-icon use');
        const actionText = this.tooltipContainer.querySelector('.contact-tooltip__action-text');
        const copyButton = this.tooltipContainer.querySelector('.contact-tooltip__copy');
        const actionButton = this.tooltipContainer.querySelector('.contact-tooltip__action');

        valueElement.textContent = displayValue;

        // Update action button
        if (isEmail) {
            actionIconContainer.className = 'contact-tooltip__action-icon ico-mail';
            actionIcon.setAttribute('xlink:href', '#ico-mail');
            actionText.textContent = this.getTranslation('sendEmail');

            // Set accessible labels
            copyButton.setAttribute('aria-label', `${displayValue} ${this.getTranslation('copy').toLowerCase()}`);
            actionButton.setAttribute('aria-label', `${this.getTranslation('sendEmail')} ${displayValue}`);
        } else {
            actionIconContainer.className = 'contact-tooltip__action-icon ico-phone';
            actionIcon.setAttribute('xlink:href', '#ico-phone');
            actionText.textContent = this.getTranslation('call');

            // Set accessible labels
            copyButton.setAttribute('aria-label', `${displayValue} ${this.getTranslation('copy').toLowerCase()}`);
            actionButton.setAttribute('aria-label', `${this.getTranslation('call')} ${displayValue}`);
        }

        // Setup Floating UI with autoUpdate for automatic repositioning
        this.setupFloatingUI();

        // Show tooltip
        this.tooltipContainer.classList.add('contact-tooltip--visible');
        this.activeTooltip = linkElement;

        // Announce the tooltip opening
        this.liveRegion.textContent = `${this.getTranslation('optionsOpened')} ${displayValue}`;
    }

    setupFloatingUI() {
        const arrowElement = this.tooltipContainer.querySelector('.contact-tooltip__arrow');

        // Clean up previous autoUpdate if exists
        if (this.cleanup) {
            this.cleanup();
        }

        // Determine preferred placement based on link position to avoid interference
        const memberActions = this.currentTarget.closest('.member__actions');
        const allContactLinks = memberActions ? Array.from(memberActions.querySelectorAll('a[href^="mailto:"], a[href^="tel:"]')) : [];
        const currentIndex = allContactLinks.indexOf(this.currentTarget);
        const isBottomLink = currentIndex > 0; // If not the first link, it's likely the bottom one

        // For bottom links, prefer left/right placement to avoid the top link
        const preferredPlacement = isBottomLink ? 'left' : 'top';
        const fallbackPlacements = isBottomLink
            ? ['right', 'left', 'bottom', 'top']
            : ['bottom', 'top', 'right', 'left'];

        // Use autoUpdate to automatically handle positioning on scroll, resize, etc.
        this.cleanup = autoUpdate(
            this.currentTarget,
            this.tooltipContainer,
            () => {
                computePosition(this.currentTarget, this.tooltipContainer, {
                    placement: preferredPlacement,
                    middleware: [
                        offset(12),
                        flip({
                            fallbackPlacements: fallbackPlacements,
                        }),
                        shift({
                            padding: 8,
                        }),
                        arrow({
                            element: arrowElement,
                            padding: 5,
                        }),
                    ],
                }).then(({ x, y, placement, middlewareData }) => {
                    // Apply positioning to tooltip
                    Object.assign(this.tooltipContainer.style, {
                        left: `${x}px`,
                        top: `${y}px`,
                    });

                    // Handle arrow positioning using Floating UI's arrow data
                    if (middlewareData.arrow) {
                        const { x: arrowX, y: arrowY } = middlewareData.arrow;
                        const side = placement.split('-')[0];

                        const staticSide = {
                            top: 'bottom',
                            right: 'left',
                            bottom: 'top',
                            left: 'right',
                        }[side];

                        // Position the arrow
                        Object.assign(arrowElement.style, {
                            left: arrowX != null ? `${arrowX}px` : '',
                            top: arrowY != null ? `${arrowY}px` : '',
                            right: '',
                            bottom: '',
                            [staticSide]: '-4px',
                        });

                        // Set the arrow direction based on placement
                        arrowElement.setAttribute('data-side', side);
                    }
                });
            }
        );
    }

    formatPhoneNumber(phone) {
        // If phone already starts with +41, format it nicely
        if (phone.startsWith('+41')) {
            const cleaned = phone.replace(/\D/g, '');
            if (cleaned.length === 11) { // +41 + 9 digits
                const number = cleaned.substring(2); // Remove country code
                return `+41 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5, 7)} ${number.substring(7)}`;
            }
            return phone; // Return as-is if format is unexpected
        }

        // If no country code, add +41 and format
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 9) {
            return `+41 ${cleaned.substring(0, 2)} ${cleaned.substring(2, 5)} ${cleaned.substring(5, 7)} ${cleaned.substring(7)}`;
        }
        return `+41 ${phone}`;
    }

    async copyValue() {
        try {
            await navigator.clipboard.writeText(this.currentValue);
            this.showCopyFeedback();
        } catch (err) {
            // Fallback for older browsers
            this.fallbackCopy(this.currentValue);
        }
    }

    fallbackCopy(text) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            document.execCommand('copy');
            this.showCopyFeedback();
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }

        document.body.removeChild(textArea);
    }

    showCopyFeedback() {
        const copyButton = this.tooltipContainer.querySelector('.contact-tooltip__copy');
        const originalSpan = this.tooltipContainer.querySelector('.contact-tooltip__copy-original');
        const feedbackSpan = this.tooltipContainer.querySelector('.contact-tooltip__copy-feedback');

        // Hide original text and show feedback using visibility to preserve space
        originalSpan.style.visibility = 'hidden';
        feedbackSpan.style.visibility = 'visible';
        feedbackSpan.style.display = 'block';
        copyButton.classList.add('contact-tooltip__copy--success');

        setTimeout(() => {
            // Show original text and hide feedback
            originalSpan.style.visibility = 'visible';
            feedbackSpan.style.visibility = 'hidden';
            feedbackSpan.style.display = 'none';
            copyButton.classList.remove('contact-tooltip__copy--success');
        }, 2000);
    }

    performAction() {
        // Perform the original action (mailto or tel)
        window.location.href = this.currentHref;
        this.hideTooltip();
    }

    hideTooltip() {
        this.clearHideTimeout();

        // Remove visual indicator from the link and restore original aria-label
        if (this.activeTooltip) {
            this.activeTooltip.classList.remove('tooltip-active');

            // Set aria-label to describe the show action
            const isEmail = this.currentType === 'email';
            const displayValue = isEmail ? this.currentValue : this.formatPhoneNumber(this.currentValue);
            this.activeTooltip.setAttribute('aria-label', `${this.getTranslation('showContactOptions')} ${displayValue}`);
            this.activeTooltip.setAttribute('aria-expanded', 'false');
            this.activeTooltip.removeAttribute('aria-controls');

            // Announce the tooltip closing
            this.liveRegion.textContent = `${this.getTranslation('optionsClosed')} ${displayValue}`;
        }

        // Clean up autoUpdate
        if (this.cleanup) {
            this.cleanup();
            this.cleanup = null;
        }

        if (this.tooltipContainer) {
            this.tooltipContainer.classList.remove('contact-tooltip--visible');
            this.activeTooltip = null;
            this.currentTarget = null;
        }
    }

    destroy() {
        // Clean up event listeners and DOM elements
        this.hideTooltip();

        if (this.tooltipContainer) {
            this.tooltipContainer.remove();
            this.tooltipContainer = null;
        }

        if (this.liveRegion) {
            this.liveRegion.remove();
            this.liveRegion = null;
        }

        // Clear any pending timeouts
        this.clearHideTimeout();
    }
}

// Initialize tooltips
const handleTooltips = () => {
    new ContactTooltip();
};

export default handleTooltips;
